import { useEffect, useRef } from "react";
import { Clock, Truck } from "lucide-react";
import { fmtDate } from "../../lib/api";
import { ActionButton, SelectInput } from "../common";

const tripFor = (d, assetId) => (d.tank_trips || []).find((t) => t.asset_id === assetId);

export const TankTripPanel = ({ d, user, busy, onTrip, trucks = [], trailers = [], onEquip, defaults }) => {
  const acceptedByMe = (d.drivers_accepted || []).includes(user?.name) || (d.driver_accepted && !(d.drivers_accepted || []).length);
  const appliedRef = useRef(new Set());
  useEffect(() => {
    if (!acceptedByMe || d.job_type === "return_pickup" || d.return_to_yard) return;
    if (d.truck_asset_id || d.trailer_asset_id) return;
    const dt = defaults?.truck_asset_id;
    const dtr = defaults?.trailer_asset_id;
    if ((!dt && !dtr) || appliedRef.current.has(d.id)) return;
    appliedRef.current.add(d.id);
    const patch = {};
    if (dt) patch.truck_asset_id = dt;
    if (dtr) patch.trailer_asset_id = dtr;
    onEquip(d, patch);
  }, [d, acceptedByMe, defaults, onEquip]);
  if (!acceptedByMe || d.job_type === "return_pickup" || d.return_to_yard) return null;
  const tanks = (d.asset_ids || []).map((id, i) => ({ id, tag: (d.asset_tags || [])[i] || id }));
  if (!tanks.length) return null;

  return (
    <div data-testid={`mobile-driver-trips-${d.id}`} className="rounded-md border border-slate-300 bg-white p-3 space-y-3 mt-2">
      <div data-testid={`mobile-driver-equip-${d.id}`} className="grid grid-cols-2 gap-2">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">My Truck</p>
          <SelectInput testId={`mobile-driver-truck-${d.id}`} value={d.truck_asset_id || defaults?.truck_asset_id || ""} onChange={(e) => onEquip(d, { truck_asset_id: e.target.value })}
            options={[{ value: "", label: "Select truck…" }, ...trucks.map((a) => ({ value: a.id, label: a.label }))]} />
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">My Trailer (optional)</p>
          <SelectInput testId={`mobile-driver-trailer-${d.id}`} value={d.trailer_asset_id || defaults?.trailer_asset_id || ""} onChange={(e) => onEquip(d, { trailer_asset_id: e.target.value })}
            options={[{ value: "", label: "No trailer (optional)" }, ...trailers.map((a) => ({ value: a.id, label: a.label }))]} />
        </div>
      </div>
      <p className="text-xs font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Truck size={13} /> Delivery Trip Time — per tank</p>
      {tanks.map(({ id, tag }) => {
        const t = tripFor(d, id) || {};
        const busyKey = `${d.id}:${id}`;
        const isBusy = busy === busyKey;
        const started = !!t.picked_up_at;
        const ended = !!t.dropped_off_at;
        return (
          <div key={id} data-testid={`mobile-driver-trip-${d.id}-${id}`} className="rounded border border-slate-200 p-2.5 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-mono text-sm text-slate-800">{tag}</span>
              {ended && (
                <span data-testid={`mobile-driver-trip-hours-${d.id}-${id}`} className="text-xs font-mono font-semibold text-emerald-700 flex items-center gap-1">
                  <Clock size={12} /> {t.trip_hours} hrs
                </span>
              )}
            </div>
            {started && <p className="text-[11px] text-slate-500" data-testid={`mobile-driver-trip-pickup-${d.id}-${id}`}>Picked-up: {fmtDate(t.picked_up_at)}</p>}
            {ended && <p className="text-[11px] text-slate-500" data-testid={`mobile-driver-trip-dropoff-${d.id}-${id}`}>Dropped-off: {fmtDate(t.dropped_off_at)}</p>}

            {!started && (
              <ActionButton testId={`mobile-driver-trip-start-${d.id}-${id}`} onClick={() => onTrip(d, id, "start")} disabled={isBusy} className="w-full justify-center !py-2.5">
                {isBusy ? "Saving…" : "Start Delivery Time – Picked-up"}
              </ActionButton>
            )}
            {started && !ended && (
              <div className="space-y-1.5">
                <ActionButton testId={`mobile-driver-trip-end-${d.id}-${id}`} onClick={() => onTrip(d, id, "end")} disabled={isBusy} className="w-full justify-center !py-2.5">
                  {isBusy ? "Saving…" : "End Delivery Time – Dropped-off"}
                </ActionButton>
                <button type="button" data-testid={`mobile-driver-trip-reset-${d.id}-${id}`} onClick={() => onTrip(d, id, "start")} disabled={isBusy} className="w-full text-[11px] font-mono uppercase tracking-wider text-slate-400 hover:text-amber-600">
                  Re-tap pick-up time
                </button>
              </div>
            )}
            {ended && (
              <div className="flex gap-2">
                <button type="button" data-testid={`mobile-driver-trip-redo-${d.id}-${id}`} onClick={() => onTrip(d, id, "start")} disabled={isBusy} className="flex-1 text-[11px] font-mono uppercase tracking-wider text-slate-400 hover:text-amber-600 border border-slate-200 rounded py-1.5">
                  Redo pick-up
                </button>
                <button type="button" data-testid={`mobile-driver-trip-reend-${d.id}-${id}`} onClick={() => onTrip(d, id, "end")} disabled={isBusy} className="flex-1 text-[11px] font-mono uppercase tracking-wider text-slate-400 hover:text-amber-600 border border-slate-200 rounded py-1.5">
                  Reset drop-off
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
